<template>
  <div>
    <ModalBase :show-modal="showModal" :mobile-full-screen="mobileView" :closeable="!mobileView" @close-modal="close" :auto-h="true" :narrow="true" :overlay-scrollb="false" class="bank-connection-modal">
        <template v-slot:titlebar>
          <TitleBar variant-class="title-modal title-modal--simple">
            <template v-slot:title>
              {{ $gettext('Link your bank transactions') }}
            </template>
          </TitleBar>
        </template>
        <template v-slot:maincontent>
          <div class="content">
            <div>
              <AnimationItem class="anim-connection" :animation="{ animationData: connectionAnimation.default }" :key="'anim_connection'" />
            </div>
            <div class="mb-3 text-center" style="display: flex; justify-content: center;">
              <BankCountrySelector
                :preselect="{ code: selectedCountry }"
                @change="(new_country) => { selectedCountry = new_country }"
              />
            </div>
            <div v-if="getCurrentCompany.country !== 'BE'" style="display: flex; justify-content: center; margin-bottom: 2rem;">
              <BankProviderSelector
                :selected-country="selectedCountry"
                @change="bankSelected"
              />
            </div>
            <p v-if="getCurrentCompany.country === 'BE'" v-translate>
              Insert your international bank account number (IBAN). By connecting with your bank you will be able to link you accounting with your bank transactions.
            </p>
            <p v-if="isBeta" class="beta-warn text-center" v-translate>
              For this country, banking is still in beta. This means that although the data is transferred securely, there could be issues with representation of the data from your bank.
            </p>
            <div v-if="getCurrentCompany.country === 'BE'" class="bank-list-wrap" :class="{ loading: tinkloading }">
              <div class="iban-form-container">
                <LoadingSpinner v-if="unifiedpostLoading" />
                <div class="form-group-container">
                  <b-form-group label-class="gk-label-margin gk-modal-label" :label="$gettext('IBAN')" label-for="input-1">
                    <b-form-input id="input-1" v-model="beIban" :placeholder="$gettext('BE0000000000001')" required></b-form-input>
                  </b-form-group>
                </div>
                <div class="ml-2 mb-1">
                  <GekkoButton @click="grantConsent()" :text="$gettext('Connect to bank')"></GekkoButton>
                </div>
              </div>
            </div>
            <!-- rabobank banner -->
            <!-- <div v-if="selectedCountry === 'NL'" class="rabobank-banner">
              <h4>{{ $gettext('Try a free business account from Rabobank') }}</h4>
              <p>
                {{ $gettext('If you don\'t have a business account yet, try the flexible business account of our partner Rabobank. It is by far the least expensive Dutch business account we could find because you only pay if you actually use the account. No fixed monthly fees, you only pay a small fee per transaction. And it takes only minutes to startup.') }}
              </p>
              <p>
                {{ $gettext('The perfect account for any Gekko user.') }}
              </p>
              <GekkoButton :href="'https://getgekko.com/en/api/v3/banks/rabobank_signup_link/'" :text="$gettext('Rabobank ZZP account')" />
            </div> -->

            <!-- gekko tip -->
            <div class="gk-tip gk-tip--blue text-center">
              <p v-translate>
                If your bank does not offer a real-time connection, then try opening a business account at internet bank N26. If you follow this link, you can setup your own business account within minutes and complete free. Not monthly subscription payments or transaction fees.
              </p>
              <a target="_blank" :href="$gettext('https://n26-eu.c2nwa3.net/c/2083015/670552/10375')">START WITH N26</a>
            </div>
          </div>
        </template>
        <template v-slot:actions>
          <GekkoButton
            @click="close"
            :text="$gettext('Cancel')"
            variant="link"
            class="text-danger px-0"
          />
        </template>
    </ModalBase>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import AnimationItem from '@/components/dashboard/ui/AnimationItem.vue'
import * as connectionAnimation from '@/css/animations/bank-connection.json';

import GekkoButton from '@/components/GekkoButton';
import ModalBase from "@/components/base_components/ModalBase";
import TitleBar from "@/components/TitleBar";
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import BankCountrySelector from "@/components/bank/ui/BankCountrySelector"
import BankProviderSelector from "@/components/bank/ui/BankProviderSelector"

import TinkService from "@/store/services/tink.service.js";
import BankService from "@/store/services/bank.service.js";

export default {
  name: "BankConnectionModal",
  components: { 
    GekkoButton, 
    ModalBase, 
    TitleBar, 
    LoadingSpinner,
    AnimationItem,
    BankCountrySelector,
    BankProviderSelector
  },
  props: {
    showModal: {
      type: Boolean,
      default: false
    },
    existingBeIban: {
      type: String,
      default: null
    },
    mobileView: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      selectedCountry: 'NL',
      bankList: null,
      tinkloading: true,
      countries: [
        { name: 'Netherlands', code: 'NL' },
        { name: 'Belgium', code: 'BE' },
        { name: 'Spain (beta)', code: 'ES', beta: true }
      ],
      beIban: '',
      unifiedpostLoading: true,
      connectionAnimation: connectionAnimation,
    }
  },
  watch: {
    existingBeIban (newVal) {
      if (newVal) {
        this.beIban = newVal;
      }
    }
  },
  computed: {
    ...mapGetters ({ 
      isPremiumUser: "getIsPremium", // root getter
      tinkCredentials: "bankStore/getTinkCredentials",
      getCurrentCompany: "main/getCurrentCompany",
      getCompanyId: 'main/getCompanyId',
    }),
    isBeta () {
      return this.countries.find(o => (o.code === this.selectedCountry && o.beta))
    }
  },
  created () {
    this.getBankList()
    this.selectedCountry = "NL";
    if (['NL', 'BE', 'ES'].includes(this.getCurrentCompany.country)){
      this.selectedCountry = this.getCurrentCompany.country
    }
    
    if (this.existingBeIban) {
      this.beIban = this.existingBeIban
    }
    this.unifiedpostLoading = false
  },
  methods: {
    close () {
      document.body.classList.remove('modal-on')
      this.$emit('close', false)
    },
    getBankList () {
      this.bankList = null
      this.tinkloading = true
      TinkService.retrieveCountry().then((response) => {
        this.tinkloading = false
        this.bankList = response.data
        this.bankList["NL"].unshift(this.bankList["DE"][0]);
      })
    },
    countrySelect () {
      if (!this.bankList) {
        this.getBankList()
      }
    },
    bankSelected (code) {
      let market = this.selectedCountry

      if(!this.isPremiumUser && this.tinkCredentials) { // multiple accounts, only for premium users
        this.$store.commit('togglePremiumModal', 'web') // TODO Check web_bank popup
      } else {
        if(code === "de-n26-ob"){
          market = "DE";
        }
        this.redirectToBank(market, code)
      }
    },
    redirectToBank (market, code) {
      const params = {
        'market': market,
        'provider': code,
        'company_profile': this.getCompanyId,
      }
      TinkService.signup(params).then((response) => {
        window.location.href = response.data;
      })
    },
    grantConsent () {
      let data = {
        iban: this.beIban,
        company_profile: this.getCompanyId
      }

      this.unifiedpostLoading = true;

      BankService.grantUnifiedpostConsent(data).then((response) => {
        location.href = response.data['url'];
      }).catch((response) => {
        this.unifiedpostLoading = false;

        if (response.status === 404) {
          this.$store.commit('setAlert', {
            title: this.$gettext("Error"),
            text: this.$gettext("IBAN not found!"),
            variant: 'danger',
            duration: 4
          });
        } else if (response.status === 401) {
          this.$store.commit('setAlert', {
            title: this.$gettext("Authentication error"),
            text: this.$gettext("Your bank account refused to connect."),
            variant: 'danger',
            duration: 4
          });
        } else if (response.status === 400) {
          this.$store.commit('setAlert', {
            title: this.$gettext("Error connecting to bank"),
            text: this.$gettext("The IBAN provided is already in use or the bank account is not available."),
            variant: 'danger',
            duration: 4
          });
        } else {
          this.$store.commit('setAlert', {
            title: this.$gettext("Error"),
            text: this.$gettext("Something went wrong in the authentication process."),
            variant: 'danger',
            duration: 4
          });
        }
      });
    }
  }
}
</script>

<style lang="scss">
.bank-connection-modal .gk-modal {
  max-width: 600px !important;
}
.bank-connection-modal .gk-modal-mobile-fullscreen {
  .gk-modal {
    max-width: 100% !important;
    min-height: 100% !important;
    padding: 1rem !important;
  }
}
.bank-connection-modal {
  .anim-connection {
    text-align: center;
    max-width: 400px;
    margin-top: -1.5rem !important;
    margin-bottom: -1.5rem !important;
  }
  .rabobank-banner {
    border-radius: $radiusLg;
    border: 1px solid $colorBorderDash;
    padding: 1rem;
    text-align: center;
    h3 { margin-bottom: 1rem; }
    p { color: $colorTextSemi; }
  }
}

</style>
<style lang="scss" scoped>
.content {
  font-size: 16px;
  .form-group {
    margin-bottom: 1.5rem;
    .country-select {
      max-width: 250px;
      // &:focus { box-shadow: none; }
    }
  }

  .beta-warn {
    color: $colorWarning;
    font-size: 14px,
  }
}
.bank-list-wrap {
  position: relative;
  &.loading { padding: 1rem 0; }
  margin-bottom: 1.5rem;
  .bank-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    grid-gap: 10px;
    grid-auto-rows: max-content;
    width: 100%;
    &__item {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 0.5rem;
      border: 1px solid $colorBorderDash;
      border-radius: 8px;
      cursor: pointer;
      background: white;
      transition: all 0.2s ease;
      &:hover {
        background-color: $colorBlue100;
        border-color: $colorBlue100;
        text-decoration: none;
      }
      &--image {
        img {
          width: 50px;
        }
        margin-bottom: 4px;
      }
      &--name {
        text-align: center;
        line-height: 1.2;
      }
    }
  }
}

.iban-form-container {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.form-group-container {
  width: 100%;
}
</style>